import { useEffect, useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import usePagination from "@/hooks/useSyncedPagination";
import { useDebounce } from "@/hooks/useDebounce";
import { ActionDataBo } from "@/types/tableAction";
import { useExpeditionFilters } from "@/components/backOffice/pendingExpeditions/useExpeditionFilters.reducer";
import { useGetAllExpeditionBo } from "@/api/expedition.api";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { FiltersPendingCustomQuotations } from "@/components/backOffice/pendingCustomQuotation/filtersPendingCustomQuotation";
import { DeleteBoModal } from "@/components/shared/modal/delete.modal";
import { NotificationModal } from "@/components/shared/modal/notification.modal";
import { CustomQuotationQuoteBoModal } from "@/components/backOffice/pendingCustomQuotation/modal/customQuotationQuoteBo.modal";
import { DetailModalBo } from "@/components/shared/detail.modal";
import { CustomQuotationsBoxTable } from "@/components/backOffice/customQuotationsBox/customQuotationsBoxTable";
import { toEndOfDayIso, toStartOfDayIso } from "@/lib/utils";

export const CustomQuotationsBoxPage = () => {
  const { t } = useTranslation("boQuotations");
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);
  const [pagination, setPagination] = usePagination("customQuotationsBox");
  const { filters, actions } = useExpeditionFilters();
  const debouncedFilters = useDebounce(filters, 300);

  const { data: expeditions } = useGetAllExpeditionBo({
    processStatus: [
      ExpeditionProcessStatusEnum.PENDING_QUOTE,
      ExpeditionProcessStatusEnum.PENDING_QUOTE_ACCEPTATION,
    ],
    quotationType: "CUSTOM_QUOTATION",
    createdAfter: debouncedFilters.createdAt?.[0]
      ? toStartOfDayIso(debouncedFilters.createdAt[0])
      : undefined,
    createdBefore: debouncedFilters.createdAt?.[1]
      ? toEndOfDayIso(debouncedFilters.createdAt[1])
      : undefined,
    search: debouncedFilters.search,
    ...(debouncedFilters.searchExact ? { searchExact: true } : {}),
    country: debouncedFilters.country,
    ...(debouncedFilters.customId ? { customId: debouncedFilters.customId } : {}),
    ...(debouncedFilters.customQuotationType && {
      customQuotationType: debouncedFilters.customQuotationType,
    }),
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data || [], [expeditions]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    debouncedFilters.search,
    debouncedFilters.searchExact,
    debouncedFilters.country,
    debouncedFilters.customId,
    debouncedFilters.customQuotationType,
    debouncedFilters.createdAt,
    setPagination,
  ]);

  return (
    <div className="bg-border/20 flex h-fit min-h-dvh w-full flex-1 flex-col gap-4 p-4 sm:p-8">
      <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
        <div className="flex w-fit flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">{t("page.title")}</h1>
          <p>{t("page.subtitle")}</p>
        </div>
      </div>
      <FiltersPendingCustomQuotations filters={filters} actions={actions} />
      <CustomQuotationsBoxTable
        data={expeditionsData}
        rowCount={expeditions?.count || 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        setActionData={setActionData}
      />
      {actionData && actionData.action === "DELETE" && (
        <DeleteBoModal
          expedition={actionData.expedition}
          open={true}
          onClose={() => setActionData(null)}
          bypassNote
        />
      )}
      {actionData && actionData.action === "FINALIZE" && (
        <CustomQuotationQuoteBoModal
          expeditionRow={actionData.expedition}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
      {actionData && actionData.action === "NOTIFICATION" && (
        <NotificationModal
          expeditionId={actionData.expedition.id}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
      {actionData && actionData.action === "DETAIL" && (
        <DetailModalBo
          expeditionId={actionData.expedition.id}
          open={true}
          companyId={actionData.expedition.companyId}
          onClose={() => setActionData(null)}
        />
      )}
    </div>
  );
};
